import { useCallback, useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@sg/garnish';

export const useOrderPlaybackCta = (props: UseOrderPlaybackCtaProps) => {
  const { fadeIn, animationDuration } = props;

  const ctaRef = useRef<any>(null);
  const [ctaWidth, setCtaWidth] = useState(0);
  const handleOnLayout = useCallback((event: LayoutChangeEvent) => {
    setCtaWidth(event.nativeEvent.layout.width);
  }, []);

  const animationProgress = useSharedValue(0);

  const backgroundAnimatedStyle = useAnimatedStyle(() => {
    return {
      width: interpolate(animationProgress.value, [0, 1], [0, ctaWidth]),
      opacity: interpolate(animationProgress.value, [0, 1], [0, 1]),
    };
  }, [ctaWidth]);

  const textAnimatedStyle = useAnimatedStyle(() => {
    return {
      color: interpolateColor(
        animationProgress.value,
        [0, 0.7],
        [theme.colors.CHARCOAL, theme.colors.WHITE],
      ),
    };
  }, []);

  const firstIconAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animationProgress.value, [0, 0.85, 1], [1, 1, 0]),
    };
  }, []);

  const secondIconAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animationProgress.value, [0, 0.85, 1], [0, 0, 1]),
    };
  }, []);

  useEffect(() => {
    if (ctaWidth === 0) return;

    animationProgress.value = withDelay(
      fadeIn,
      withTiming(1, { duration: animationDuration }),
    );
  }, [ctaWidth, fadeIn, animationDuration, animationProgress]);

  return {
    ctaRef,
    handleOnLayout,
    backgroundAnimatedStyle,
    textAnimatedStyle,
    firstIconAnimatedStyle,
    secondIconAnimatedStyle,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOrderPlaybackCtaProps = {
  fadeIn: number;
  animationDuration: number;
};
